import { Stack } from '@mui/material';
import { styled } from '@mui/material/styles';
import { Box } from '@mui/system';
import { Navigate, Outlet, useMatchRoute } from '@tanstack/react-router';
import Logo from 'components/Logo';
import { PageLayoutCancelLink } from 'components/link/PageLayoutCancelLink';
import { useCurrentPharmacy, useCurrentRole, useCurrentUser } from 'hooks/slices';
import { isEmpty, isNil } from 'lodash';
import { getPharmacyVerificationState, PharmacyVerificationState } from 'utils/onboarding';

const HeaderStyle = styled('header')(({ theme }) => ({
  position: 'absolute',
  top: 0,
  left: 0,
  width: '100%',
  padding: theme.spacing(3, 3, 0),
  [theme.breakpoints.up('sm')]: {
    padding: theme.spacing(5, 5, 0),
  },
}));

const OnboardingLayout = () => {
  const user = useCurrentUser();
  const role = useCurrentRole();
  const pharmacy = useCurrentPharmacy();
  const matchRoute = useMatchRoute();

  const shouldSignOut = !!(
    matchRoute({ to: '/onboarding/pharmacist_or_lab' }) ||
    matchRoute({ to: '/onboarding/search_pharmacy' }) ||
    matchRoute({ to: '/onboarding/verify_email' }) ||
    matchRoute({ to: '/onboarding/verify_account' })
  );

  const pharmacyVerificationState = getPharmacyVerificationState(
    pharmacy?.verified || false,
    role?.kind
  );

  if (!user) return <Navigate to="/login/phone_number" />;

  // Hacky solution to mark profile creation step
  const isProfileCreated = !isEmpty(user?.first_name);

  // Prevents the user from skipping onboarding steps
  if (
    !isProfileCreated &&
    !matchRoute({ to: '/onboarding/create_profile' }) &&
    !matchRoute({ to: '/onboarding/pharmacist_or_lab' }) &&
    !matchRoute({ to: '/onboarding/lab_unavailable' })
  )
    return <Navigate to="/onboarding/pharmacist_or_lab" />;

  // Prevents the user from going back on the onboarding steps
  if (
    isProfileCreated &&
    isNil(role) &&
    !matchRoute({ to: '/onboarding/search_pharmacy' }) &&
    !matchRoute({ to: '/onboarding/create_pharmacy' }) &&
    !matchRoute({ to: '/onboarding/join_pharmacy' })
  ) {
    return <Navigate to="/onboarding/search_pharmacy" />;
  }

  if (
    pharmacyVerificationState === PharmacyVerificationState.email &&
    !matchRoute({ to: '/onboarding/verify_email' })
  )
    return <Navigate to="/onboarding/verify_email" />;

  if (
    pharmacyVerificationState === PharmacyVerificationState.phoneBack &&
    !matchRoute({ to: '/onboarding/verify_account' })
  )
    return <Navigate to="/onboarding/verify_account" />;

  return (
    <Box sx={{ minHeight: '100vh' }}>
      <HeaderStyle>
        <Stack flexDirection="row">
          <Logo />
          <Box style={{ alignSelf: 'flex-end', position: 'relative', bottom: 7 }}>
            <PageLayoutCancelLink
              shouldSignOut={shouldSignOut}
              onSignOutNavigateTo="/login/phone_number"
            />
          </Box>
        </Stack>
      </HeaderStyle>
      <Outlet />
    </Box>
  );
};

export default OnboardingLayout;
